import { Wishlist } from '@theme/lib-wishlist';
import { ThemeEvent } from '@theme/events';
import { isDesktop, isTablet } from '@theme/utilities';
import { buildProductCardPriceMarkup } from '@theme/lib-product-card-price';

/** @typedef {{ id: string, title?: string, image?: string, price?: string, url?: string }} WishlistStoredItem */

/**
 * @typedef {Object} WishlistProductVariant
 * @property {string|number} id
 * @property {boolean} available
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {string[]} options
 */

/**
 * @typedef {Object} WishlistProductData
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {boolean} available
 * @property {string} description
 * @property {string} featuredImage
 * @property {WishlistProductVariant[]} variants
 * @property {string[]} optionNames
 * @property {boolean} hasOnlyDefaultVariant
 * @property {WishlistProductVariant|null} selectedVariant
 * @property {number} [price_min]
 * @property {number} [price_max]
 * @property {boolean} [price_varies]
 * @property {number} [compare_at_price_min]
 * @property {number} [compare_at_price_max]
 */

/**
 * @typedef {Object} WishlistBlockConfig
 * @property {'style-1' | 'style-2'} productCardStyle
 * @property {boolean} showQuickView
 * @property {boolean} showAddToCart
 * @property {string} quickViewLabel
 * @property {string} selectOptionsLabel
 * @property {string} addToCartLabel
 * @property {string} addingToCartLabel
 * @property {string} addedToCartMessage
 * @property {string} addToCartErrorMessage
 * @property {string} decreaseQuantityLabel
 * @property {string} increaseQuantityLabel
 * @property {string} closeDialogLabel
 * @property {string} unavailableVariantLabel
 */

/** @type {Map<string, Promise<WishlistProductData|null>>} */
const productCache = new Map();

/** @type {WeakMap<HTMLElement, number>} */
const renderTokens = new WeakMap();

const PRODUCT_CARD_IMAGE_WIDTH = 1080;
const PRODUCT_CARD_IMAGE_WIDTHS = [360, 540, 720, 900, 1080];
const PRODUCT_CARD_IMAGE_SIZES =
  '(min-width: 1280px) calc((100vw - 8rem) / 4), (min-width: 880px) calc((100vw - 6rem) / 4), (min-width: 640px) calc((100vw - 4rem) / 3), calc((100vw - 3rem) / 1.5)';

const CART_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="product-card__mobile-action-icon product-card__stack-action-icon" aria-hidden="true"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.72a2 2 0 0 0 2-1.58L23 6H6"></path></svg>';

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * @param {unknown} value
 * @param {number} maxLength
 * @returns {string}
 */
function truncateText(value, maxLength) {
  const text = String(value || '').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

/**
 * @returns {string}
 */
function getCurrencyCode() {
  return window.Shopify?.currency?.active || 'USD';
}

/**
 * @param {number} cents
 * @returns {string}
 */
function formatMoneyValue(cents) {
  const numericCents = Number(cents);
  if (Number.isNaN(numericCents)) return '';

  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: getCurrencyCode(),
    }).format(numericCents / 100);
  } catch (error) {
    return (numericCents / 100).toFixed(2);
  }
}

/**
 * @param {string} imageUrl
 * @param {number} [width]
 * @returns {string}
 */
function buildResponsiveImageUrl(imageUrl, width = PRODUCT_CARD_IMAGE_WIDTH) {
  if (!imageUrl) return '';

  try {
    const url = new URL(imageUrl, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return imageUrl;
  }
}

/**
 * @param {string} imageUrl
 * @returns {string}
 */
function buildResponsiveImageSrcSet(imageUrl) {
  if (!imageUrl) return '';

  return PRODUCT_CARD_IMAGE_WIDTHS.map((width) => {
    return `${buildResponsiveImageUrl(imageUrl, width)} ${width}w`;
  }).join(', ');
}

/**
 * @param {HTMLImageElement} imageNode
 * @param {string} imageUrl
 * @param {string} altText
 */
function applyResponsiveProductImage(imageNode, imageUrl, altText) {
  imageNode.src = buildResponsiveImageUrl(imageUrl);
  imageNode.srcset = buildResponsiveImageSrcSet(imageUrl);
  imageNode.sizes = PRODUCT_CARD_IMAGE_SIZES;
  imageNode.alt = altText;
  imageNode.loading = 'lazy';
  imageNode.decoding = 'async';
}

/**
 * @param {HTMLElement} blockRoot
 * @returns {WishlistBlockConfig}
 */
function getBlockConfig(blockRoot) {
  return {
    productCardStyle:
      blockRoot.dataset.productCardStyle === 'style-2' ? 'style-2' : 'style-1',
    showQuickView: blockRoot.dataset.showQuickview === 'true',
    showAddToCart: blockRoot.dataset.showAddToCart === 'true',
    quickViewLabel: blockRoot.dataset.labelQuickView || 'Quick view',
    selectOptionsLabel:
      blockRoot.dataset.labelSelectOptions || 'Select options',
    addToCartLabel: blockRoot.dataset.labelAddToCart || 'Add to cart',
    addingToCartLabel:
      blockRoot.dataset.labelAddingToCart || 'Adding to cart...',
    addedToCartMessage: blockRoot.dataset.messageAddedToCart || 'Added to cart',
    addToCartErrorMessage:
      blockRoot.dataset.messageAddToCartError ||
      'Unable to add this item right now.',
    decreaseQuantityLabel:
      blockRoot.dataset.labelDecreaseQuantity || 'Decrease quantity',
    increaseQuantityLabel:
      blockRoot.dataset.labelIncreaseQuantity || 'Increase quantity',
    closeDialogLabel: blockRoot.dataset.labelCloseDialog || 'Close dialog',
    unavailableVariantLabel:
      blockRoot.dataset.labelUnavailableVariant ||
      'This variant is currently unavailable',
  };
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeOptionValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    const option =
      /** @type {{ name?: unknown, value?: unknown, label?: unknown }} */ (
        value
      );
    return String(option.name || option.value || option.label || '');
  }
  return String(value);
}

/**
 * @param {Record<string, unknown>} variant
 * @param {number} optionCount
 * @returns {string[]}
 */
function getVariantOptions(variant, optionCount) {
  return Array.from({ length: optionCount }, (_, index) => {
    return normalizeOptionValue(variant[`option${index + 1}`]);
  });
}

/**
 * @param {Record<string, unknown>} product
 * @param {WishlistStoredItem} item
 * @returns {WishlistProductData}
 */
function normalizeProductData(product, item) {
  const optionNames = Array.isArray(product.options)
    ? product.options.map((option) => normalizeOptionValue(option))
    : [];
  const firstVariant =
    Array.isArray(product.variants) && product.variants[0]
      ? /** @type {{ options?: unknown[] }} */ (product.variants[0])
      : null;
  const variantOptionCount =
    optionNames.length || firstVariant?.options?.length || 0;
  const variants = Array.isArray(product.variants)
    ? product.variants.map((variant) => {
        const source = /** @type {Record<string, unknown>} */ (variant);
        return {
          id: /** @type {string|number} */ (source.id),
          available: Boolean(source.available),
          price: Number(source.price),
          compare_at_price: Number(source.compare_at_price),
          options: getVariantOptions(source, variantOptionCount),
        };
      })
    : [];
  const selectedVariant =
    variants.find((variant) => variant.available) || variants[0] || null;

  return {
    id: String(product.id || item.id || ''),
    title: String(product.title || item.title || 'Product'),
    url: item.url || String(product.url || '#'),
    available: Boolean(product.available),
    description: truncateText(
      String(product.description || '').replace(/<[^>]+>/g, ' '),
      100
    ),
    featuredImage:
      String(product.featured_image || '') ||
      (Array.isArray(product.images) ? String(product.images[0] || '') : '') ||
      item.image ||
      '',
    variants,
    optionNames,
    hasOnlyDefaultVariant:
      variants.length <= 1 && (!optionNames[0] || optionNames[0] === 'Title'),
    selectedVariant,
    price_min: Number(product.price_min ?? product.price ?? 0),
    price_max: Number(product.price_max ?? product.price ?? 0),
    price_varies: Boolean(product.price_varies),
    compare_at_price_min:
      product.compare_at_price_min == null
        ? undefined
        : Number(product.compare_at_price_min),
    compare_at_price_max:
      product.compare_at_price_max == null
        ? undefined
        : Number(product.compare_at_price_max),
  };
}

/**
 * @param {string} productUrl
 * @returns {string}
 */
function getProductJsonUrl(productUrl) {
  const url = new URL(productUrl || '#', window.location.origin);
  url.search = '';
  url.hash = '';
  url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
  return url.toString();
}

/**
 * @param {WishlistStoredItem} item
 * @returns {Promise<WishlistProductData|null>}
 */
async function fetchProductData(item) {
  const cacheKey = item.url || item.id || item.title || '';
  if (!cacheKey) return null;

  const cached = productCache.get(cacheKey);
  if (cached) return cached;

  const request = fetch(getProductJsonUrl(item.url || ''), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to load product data');
      }
      return response.json();
    })
    .then((product) =>
      normalizeProductData(
        /** @type {Record<string, unknown>} */ (product),
        item
      )
    )
    .catch(() => null);

  productCache.set(cacheKey, request);
  return request;
}

/**
 * @param {WishlistBlockConfig} config
 * @param {string} buttonClass
 * @param {string} buttonVariantClass
 * @param {string} [buttonSizeClass]
 * @param {boolean} [iconOnly]
 * @returns {string}
 */
function buildAddToCartButtonMarkup(
  config,
  buttonClass,
  buttonVariantClass,
  buttonSizeClass = 'button-size-default',
  iconOnly = false
) {
  const labelMarkup = iconOnly
    ? `${CART_ICON_SVG}<span class="visually-hidden" data-add-to-cart-label>${escapeHtml(config.addToCartLabel)}</span>`
    : escapeHtml(config.addToCartLabel);

  return `<button
    type="submit"
    class="add-to-cart-button button ${buttonVariantClass} ${buttonSizeClass} ${buttonClass}"
    data-add-to-cart-submit
    data-label-default="${escapeHtml(config.addToCartLabel)}"
    data-label-loading="${escapeHtml(config.addingToCartLabel)}"
    data-message-success="${escapeHtml(config.addedToCartMessage)}"
    data-message-error="${escapeHtml(config.addToCartErrorMessage)}"
    aria-label="${escapeHtml(config.addToCartLabel)}"
  >
    ${labelMarkup}
  </button>`;
}

/**
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 * @param {string} label
 * @param {string} buttonClass
 * @returns {string}
 */
function buildQuickViewTextButtonMarkup(product, config, label, buttonClass) {
  return `<div class="quick-view-button-wrapper quick-view-button-wrapper--text" title="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}">
    <button
      type="button"
      class="quick-view-button quick-view-button--text ${buttonClass}"
      data-quick-view-trigger
      data-product-id="${escapeHtml(product.id)}"
      data-product-url="${escapeHtml(product.url || '#')}"
      aria-haspopup="dialog"
      aria-label="${escapeHtml(config.quickViewLabel)} ${escapeHtml(product.title)}"
    >
      <span class="quick-view-button__label">${escapeHtml(label)}</span>
    </button>
  </div>`;
}

/**
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 * @returns {string}
 */
function buildCardCtaMarkup(product, config) {
  if (!config.showAddToCart || !product.available) return '';

  if (!product.hasOnlyDefaultVariant) {
    return buildQuickViewTextButtonMarkup(
      product,
      config,
      config.selectOptionsLabel,
      'product-card__cta-button button button-variant-primary button-size-default'
    );
  }

  if (!product.selectedVariant) return '';

  return `<form>
    <input type="hidden" name="id" value="${escapeHtml(product.selectedVariant.id)}">
    <input type="hidden" name="quantity" value="1">
    ${buildAddToCartButtonMarkup(
      config,
      'product-card__cta-button',
      'button-variant-default'
    )}
  </form>`;
}

/**
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 * @returns {string}
 */
function buildStackCartMarkup(product, config) {
  if (!product.available) return '';

  if (!product.hasOnlyDefaultVariant) {
    return `<div class="quick-view-button-wrapper quick-view-button-wrapper--icon" title="${escapeHtml(config.selectOptionsLabel)} ${escapeHtml(product.title)}">
      <button
        type="button"
        class="quick-view-button quick-view-button--icon product-card__stack-action-button button button-variant-default button-size-icon-sm"
        data-quick-view-trigger
        data-product-id="${escapeHtml(product.id)}"
        data-product-url="${escapeHtml(product.url || '#')}"
        aria-haspopup="dialog"
        aria-label="${escapeHtml(config.selectOptionsLabel)} ${escapeHtml(product.title)}"
      >
        ${CART_ICON_SVG}
        <span class="visually-hidden">${escapeHtml(config.selectOptionsLabel)}</span>
      </button>
    </div>`;
  }

  if (!product.selectedVariant) return '';

  return `<form>
    <input type="hidden" name="id" value="${escapeHtml(product.selectedVariant.id)}">
    <input type="hidden" name="quantity" value="1">
    ${buildAddToCartButtonMarkup(
      config,
      'product-card__stack-action-button',
      'button-variant-default',
      'button-size-icon-sm',
      true
    )}
  </form>`;
}

/**
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 * @returns {string}
 */
function buildMobileCtaMarkup(product, config) {
  if (!config.showAddToCart || !product.available) return '';

  if (!product.hasOnlyDefaultVariant) {
    return `<div class="product-card__mobile-action">
      <button
        type="button"
        class="product-card__mobile-action-button button button-variant-default button-size-icon-sm"
        data-quick-view-trigger
        data-product-id="${escapeHtml(product.id)}"
        data-product-url="${escapeHtml(product.url || '#')}"
        aria-haspopup="dialog"
        aria-label="${escapeHtml(config.selectOptionsLabel)} ${escapeHtml(product.title)}"
      >
        ${CART_ICON_SVG}
        <span class="visually-hidden">${escapeHtml(config.selectOptionsLabel)}</span>
      </button>
    </div>`;
  }

  if (!product.selectedVariant) return '';

  return `<form>
    <input type="hidden" name="id" value="${escapeHtml(product.selectedVariant.id)}">
    <input type="hidden" name="quantity" value="1">
    ${buildAddToCartButtonMarkup(
      config,
      'product-card__mobile-action-button',
      'button-variant-default',
      'button-size-icon-sm',
      true
    )}
  </form>`;
}

/**
 * @param {ParentNode} root
 * @param {WishlistStoredItem} item
 */
function syncWishlistButton(root, item) {
  root.querySelectorAll('[data-action="toggle-wishlist"]').forEach((button) => {
    if (!(button instanceof HTMLElement)) return;

    const title = item.title || 'Product';
    const addLabel = `Add ${title} to wishlist`;
    const removeLabel = `Remove ${title} from wishlist`;

    button.dataset.productId = item.id || '';
    button.dataset.productTitle = title;
    button.dataset.productImage = item.image || '';
    button.dataset.wishlistProductPrice = item.price || '';
    button.dataset.productUrl = item.url || '#';
    button.dataset.labelAdd = addLabel;
    button.dataset.labelRemove = removeLabel;
    button.setAttribute('aria-label', removeLabel);
    button.setAttribute('aria-pressed', 'true');
    button.classList.add('is-wishlisted');

    const wrapper = button.closest('.wishlist-button-wrapper');
    if (wrapper instanceof HTMLElement) {
      wrapper.title = `Toggle ${title}`;
    }
  });
}

/**
 * @param {ParentNode} root
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 */
function syncQuickViewButtons(root, product, config) {
  root
    .querySelectorAll('[data-quick-view-trigger], .quick-view-button')
    .forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.dataset.productId = product.id;
      button.dataset.productUrl = product.url || '#';
      button.setAttribute(
        'aria-label',
        `${config.quickViewLabel} ${product.title}`
      );
    });

  root.querySelectorAll('.quick-view-button-wrapper').forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.title = `${config.quickViewLabel} ${product.title}`;
  });
}

/**
 * Quick view opens by fetching the product section. Remove leftover local
 * dialog markup so the shared fetch path can run.
 *
 * @param {HTMLElement} itemRoot
 */
function removeLocalQuickViewDialogs(itemRoot) {
  itemRoot
    .querySelectorAll('dialog-content[id^="quick-view-"]')
    .forEach((node) => {
      node.remove();
    });
}

/**
 * @param {HTMLElement} itemRoot
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 */
function syncCallToAction(itemRoot, product, config) {
  const ctaRoot = itemRoot.querySelector('.product-card__call-to-action');
  if (!(ctaRoot instanceof HTMLElement)) return;

  if (!config.showAddToCart || !product.available) {
    ctaRoot.innerHTML = '';
    return;
  }

  ctaRoot.innerHTML = buildCardCtaMarkup(product, config);
}

/**
 * @param {HTMLElement} itemRoot
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 */
function syncMobileCallToAction(itemRoot, product, config) {
  const mobileCtaRoot = itemRoot.querySelector('[data-wishlist-mobile-cta]');
  if (!(mobileCtaRoot instanceof HTMLElement)) return;

  if (!config.showAddToCart || !product.available) {
    mobileCtaRoot.innerHTML = '';
    return;
  }

  mobileCtaRoot.innerHTML = buildMobileCtaMarkup(product, config);
}

/**
 * @param {HTMLElement} itemRoot
 * @param {WishlistProductData} product
 * @param {WishlistBlockConfig} config
 */
function syncStackCartAction(itemRoot, product, config) {
  const cartRoot = itemRoot.querySelector('[data-product-card-cart-action]');
  if (!(cartRoot instanceof HTMLElement)) return;

  cartRoot.innerHTML = buildStackCartMarkup(product, config);
}

/**
 * @param {HTMLElement} root
 */
function clearRenderedCards(root) {
  root.querySelectorAll('[data-wishlist-rendered="true"]').forEach((node) => {
    node.remove();
  });
}

/**
 * @param {HTMLElement} blockRoot
 * @param {WishlistStoredItem} item
 * @returns {Promise<HTMLElement|null>}
 */
async function createCard(blockRoot, item) {
  const template = blockRoot.querySelector('[data-wishlist-card-template]');
  if (!(template instanceof HTMLTemplateElement)) return null;

  const fragment = template.content.cloneNode(true);
  if (!(fragment instanceof DocumentFragment)) return null;

  const itemRoot = fragment.firstElementChild;
  if (!(itemRoot instanceof HTMLElement)) return null;

  const card = itemRoot.querySelector('.product-card');
  if (!(card instanceof HTMLElement)) return null;

  const title = item.title || 'Product';
  const productUrl = item.url || '#';
  const price = item.price || '';
  const config = getBlockConfig(blockRoot);
  const product = await fetchProductData(item);
  const imageUrl = product?.featuredImage || item.image || '';

  card.setAttribute('data-product-id', item.id || '');
  // Client-rendered cards miss the page-load IntersectionObserver that
  // adds animate-in; without it they stay opacity: 0 under .js-enabled.
  card.classList.add('animate-in');
  card.setAttribute('data-pc-animated', 'true');

  itemRoot.querySelectorAll('.product-card--link').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = productUrl;
    link.title = title;
  });

  itemRoot.querySelectorAll('.product-card-title').forEach((heading) => {
    if (!(heading instanceof HTMLElement)) return;
    heading.textContent = title;
    if (item.id) {
      heading.id = `wishlist-product-title-${item.id}`;
    }
  });

  itemRoot.querySelectorAll('.product-card__price').forEach((priceNode) => {
    if (!(priceNode instanceof HTMLElement)) return;
    priceNode.textContent = price;
  });

  syncWishlistButton(itemRoot, item);

  if (product) {
    card.setAttribute('data-product-id', product.id);

    const currentPrice = itemRoot.querySelector('.product-card__price');
    if (currentPrice instanceof HTMLElement) {
      currentPrice.innerHTML = buildProductCardPriceMarkup(product);
    }

    if (config.productCardStyle === 'style-2') {
      syncStackCartAction(itemRoot, product, config);
    } else {
      syncQuickViewButtons(itemRoot, product, config);
      syncCallToAction(itemRoot, product, config);
      syncMobileCallToAction(itemRoot, product, config);
    }
    removeLocalQuickViewDialogs(itemRoot);
  } else {
    itemRoot.querySelectorAll('.quick-view-button-wrapper').forEach((node) => {
      node.remove();
    });
    removeLocalQuickViewDialogs(itemRoot);

    const ctaRoot = itemRoot.querySelector('.product-card__call-to-action');
    if (ctaRoot instanceof HTMLElement) {
      ctaRoot.innerHTML = '';
    }

    const mobileCtaRoot = itemRoot.querySelector('[data-wishlist-mobile-cta]');
    if (mobileCtaRoot instanceof HTMLElement) {
      mobileCtaRoot.innerHTML = '';
    }

    const cartRoot = itemRoot.querySelector('[data-product-card-cart-action]');
    if (cartRoot instanceof HTMLElement) {
      cartRoot.innerHTML = '';
    }
  }

  itemRoot
    .querySelectorAll('.product-card__image-primary')
    .forEach((imageNode) => {
      if (imageNode instanceof HTMLImageElement) {
        if (!imageUrl) {
          imageNode.remove();
          return;
        }

        applyResponsiveProductImage(imageNode, imageUrl, title);
      }
    });

  if (imageUrl) {
    const placeholder = itemRoot.querySelector('.product-card__placeholder');
    if (placeholder instanceof HTMLElement) {
      const image = document.createElement('img');
      image.className = 'image--fit-cover product-card__image-primary';
      applyResponsiveProductImage(image, imageUrl, title);
      placeholder.replaceWith(image);
    }
  }

  return itemRoot;
}

/**
 * @param {HTMLElement} blockRoot
 * @param {HTMLElement} grid
 * @param {number} count
 */
function insertSkeletons(blockRoot, grid, count) {
  const skeletonTemplate = blockRoot.querySelector(
    '[data-wishlist-skeleton-template]'
  );
  if (!(skeletonTemplate instanceof HTMLTemplateElement)) return;

  const frag = document.createDocumentFragment();
  for (let i = 0; i < count; i++) {
    const clone = skeletonTemplate.content.cloneNode(true);
    if (!(clone instanceof DocumentFragment)) continue;
    const el = clone.firstElementChild;
    if (el instanceof HTMLElement) el.dataset.wishlistSkeleton = 'true';
    frag.appendChild(clone);
  }

  grid.appendChild(frag);
}

/**
 * @param {HTMLElement} blockRoot
 */
function removeSkeletons(blockRoot) {
  blockRoot
    .querySelectorAll('[data-wishlist-skeleton="true"]')
    .forEach((node) => {
      node.remove();
    });
}

/**
 * @param {HTMLElement} blockRoot
 */
async function renderBlock(blockRoot) {
  const grid = blockRoot.querySelector('[data-wishlist-grid]');
  if (!(grid instanceof HTMLElement)) return;

  const token = (renderTokens.get(blockRoot) || 0) + 1;
  renderTokens.set(blockRoot, token);

  const cs = getComputedStyle(grid);
  const mobileCols = Number(cs.getPropertyValue('--products-mobile')) || 2;
  const tabletCols =
    Number(cs.getPropertyValue('--products-tablet')) || mobileCols;
  const desktopCols =
    Number(cs.getPropertyValue('--products-desktop')) || tabletCols;

  let skeletonCount = mobileCols;
  try {
    if (isDesktop()) {
      skeletonCount = desktopCols;
    } else if (isTablet()) {
      skeletonCount = tabletCols;
    }
  } catch (error) {
    skeletonCount = mobileCols;
  }

  clearRenderedCards(grid);
  insertSkeletons(blockRoot, grid, skeletonCount);

  const items = Wishlist.getDetails();
  const emptyEl = blockRoot.querySelector('[data-wishlist-empty]');

  if (items.length === 0) {
    if (renderTokens.get(blockRoot) !== token) return;
    removeSkeletons(blockRoot);
    if (emptyEl instanceof HTMLElement) emptyEl.removeAttribute('hidden');
    return;
  }

  if (emptyEl instanceof HTMLElement) emptyEl.setAttribute('hidden', '');

  const cards = await Promise.all(
    items.map((item) => createCard(blockRoot, item))
  );

  if (renderTokens.get(blockRoot) !== token) return;

  removeSkeletons(blockRoot);

  cards.forEach((card) => {
    if (!card) return;
    card.dataset.wishlistRendered = 'true';
    grid.appendChild(card);
  });
}

function initWishlistBlocks() {
  document.querySelectorAll('.wishlist-block').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    renderBlock(root).catch(() => {
      // Keep wishlist rendering resilient when a product payload fails.
    });
  });
}

document.addEventListener('DOMContentLoaded', initWishlistBlocks);
document.addEventListener('shopify:section:load', initWishlistBlocks);
document.addEventListener('shopify:section:reorder', initWishlistBlocks);
document.addEventListener('shopify:section:select', initWishlistBlocks);
document.addEventListener('shopify:block:select', initWishlistBlocks);
window.addEventListener(ThemeEvent.WishlistChanged, initWishlistBlocks);
window.addEventListener(ThemeEvent.WishlistRefresh, initWishlistBlocks);
